import React, { useEffect, useCallback } from 'react';
import { KEY_CODES } from 'utility/constants';
import useOutsideClickDetector from 'hooks/useOutsideClickDetector';
import { useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import './SayTModalWrapper.scss';

type Props = {
  open?: boolean;
  onCloseModal: () => void;
  headerRef?: React.RefObject<HTMLDivElement | null>;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  clearButtonRef?: React.RefObject<HTMLButtonElement | null>;
  searchButtonRef?: React.RefObject<HTMLButtonElement | null>;
  viewPortBreakpoint?: string;
  children?: React.ReactNode;
};

const SAYTModalWrapper = ({
  open = false,
  onCloseModal,
  children = null,
  headerRef,
  searchInputRef,
  clearButtonRef,
  searchButtonRef,
  viewPortBreakpoint,
  ...props
}: Props) => {
  const newSayTEnabled = useAppSelector(getFeatureFlagByNameSelector('saytRedesignEnabled'));
  const modalContainerRef = useOutsideClickDetector(
    onCloseModal,
    open,
    searchInputRef ? [searchInputRef] : [],
  ) as React.RefObject<HTMLDivElement>;

  // This method is to handle up/down arrow key press to trap focus within the modal. On TAB key press, the clear button is focussed.
  // We set the tabIndex to -1 for all modal children so that they are not focusable by tab key.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (open && modalContainerRef.current) {
        const focusableElements = modalContainerRef.current.querySelectorAll(
          'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
        );
        // set tabIndex to -1 for all modal children so that they are not focusable by tab key
        focusableElements?.forEach((element) => {
          (element as HTMLElement).setAttribute('tabindex', '-1');
        });
        const focusableArray = Array.prototype.slice.call(focusableElements);
        const currentIndex = focusableArray.indexOf(document.activeElement);

        if (event.key === KEY_CODES.ARROW_DOWN) {
          event.preventDefault();
          const nextIndex = (currentIndex + 1) % focusableArray.length;
          (focusableArray[nextIndex] as HTMLElement).focus();
        } else if (event.key === KEY_CODES.ARROW_UP) {
          event.preventDefault();
          const prevIndex = (currentIndex - 1 + focusableArray.length) % focusableArray.length;
          (focusableArray[prevIndex] as HTMLElement).focus();
        } else if (event.key === KEY_CODES.TAB) {
          if (modalContainerRef.current.contains(target)) {
            event.preventDefault();
            clearButtonRef?.current?.focus();
          }
        }
      }
    },
    [open],
  );

  // This is to trap focus within the modal when it is open and only allow arrow key navigation inside the modal.
  // This runs along with the handleKeyDown method to prevent focus from going outside the modal.
  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore tab order for modal children
      const focusableElementsWithTab = modalContainerRef.current?.querySelectorAll(
        'a[href], button, input, textarea, select, details, [tabindex="-1"]',
      );
      focusableElementsWithTab?.forEach((element) => {
        (element as HTMLElement).removeAttribute('tabindex');
      });
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown]);

  // This method is to close the modal when pressing the escape key
  useEffect(() => {
    const handleEscKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEY_CODES.ESCAPE) {
        onCloseModal();
      }
    };
    document.addEventListener('keydown', handleEscKeyDown);
    return () => {
      document.removeEventListener('keydown', handleEscKeyDown);
    };
  }, [onCloseModal]);

  // This is to dynamically position the modal based on the search bar position.
  // screen.orientation is used to get the width of the search bar when the device is rotated.
  useEffect(() => {
    const searchBarElement = document.getElementById('searchBarContainer');
    const calculateModalPosition = () => {
      if (searchBarElement && modalContainerRef.current) {
        const searchBarRect = searchBarElement.getBoundingClientRect();
        const searchBarWidth = searchBarRect.width;
        const searchBarLeft = searchBarRect.left;
        if (viewPortBreakpoint !== 'mobile') {
          modalContainerRef.current.style.width = `${searchBarWidth}px`;
          modalContainerRef.current.style.left = `${searchBarLeft}px`;
        } else {
          modalContainerRef.current.style.width = '100%';
          modalContainerRef.current.style.left = '0px';
        }
      }
    };
    calculateModalPosition();

    const resizeObserver = new ResizeObserver(() => {
      calculateModalPosition();
    });

    if (document.body) {
      resizeObserver.observe(document.body);
    }
    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', calculateModalPosition);
    }
    return () => {
      if (document.body) {
        resizeObserver.unobserve(document.body);
      }
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', calculateModalPosition);
      }
    };
  }, [viewPortBreakpoint]);

  // This is to close the modal when focus is moved outside of the modal and the search bar
  useEffect(() => {
    const handleFocusOut = (event: FocusEvent) => {
      const target = event.relatedTarget as HTMLElement;
      if (target && document.body.contains(target)) {
        if (
          target !== searchInputRef?.current &&
          target !== clearButtonRef?.current &&
          target !== searchButtonRef?.current &&
          !modalContainerRef.current?.contains(target)
        ) {
          onCloseModal();
        }
      }
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [onCloseModal]);

  // This is to set the height of the wrapper component to the scrollable height of the page
  useEffect(() => {
    const pageHeight = document.body.scrollHeight;
    const parentContainer = document.getElementsByClassName(
      'sayt-modal-container-wrapper',
    )[0] as HTMLElement;

    if (open) {
      parentContainer.style.height = `${pageHeight}px`;
    }
  }, [open]);

  // This is to close the modal when the user scrolls past the modal.
  // We have created two intersection observers to handle this. One for the modal and one for the search bar.
  // When the modal is not intersecting with the viewport, the modal is closed.
  // When the search bar is not intersecting with the viewport, the search input is blurred.
  useEffect(() => {
    if (open) {
      let isSearchBarIntersecting = true;
      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !isSearchBarIntersecting) {
            onCloseModal();
          }
        });
      };
      const handleSearchBarIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          isSearchBarIntersecting = entry.isIntersecting;
          if (!entry.isIntersecting) {
            searchInputRef?.current?.blur();
          }
        });
      };
      const modalObserver = new IntersectionObserver(handleIntersection, {
        root: null, // Use the viewport as the root
        threshold: 0, // Trigger when the modal is not intersecting with the viewport
      });
      const searchBarObserver = new IntersectionObserver(handleSearchBarIntersection, {
        root: null, // Use the viewport as the root
        threshold: 0, // Trigger when the search bar is not intersecting with the viewport
      });

      if (searchInputRef?.current) {
        searchBarObserver.observe(searchInputRef.current);
      }

      if (modalContainerRef.current) {
        modalObserver.observe(modalContainerRef.current);
      }

      return () => {
        if (searchInputRef?.current) {
          searchBarObserver.unobserve(searchInputRef.current);
        }
        if (modalContainerRef.current) {
          modalObserver.unobserve(modalContainerRef.current);
        }
      };
    }
    return undefined;
  }, [open, onCloseModal, viewPortBreakpoint]);

  // This logic is for mobile and tablet on PLP/SRLP pages only when the search bar is sticky.
  // This sets the max-height of the modal to the visual viewport height and allows scroll inside the modal content
  // Visual viewport is used to get the height of the viewport when the native mobile keyboard is open.
  // screen.orientation is used to get the height of the visual viewport when the device is rotated.
  useEffect(() => {
    const searchBarSticky = document.querySelectorAll(
      '.dsg-header-search-sticky-enabled',
    )[0] as HTMLElement;

    const updateModalHeight = () => {
      if (
        open &&
        (viewPortBreakpoint === 'mobile' || viewPortBreakpoint === 'tablet') &&
        searchBarSticky.classList.contains('sticky') &&
        modalContainerRef.current
      ) {
        const visualViewportHeight = window.visualViewport?.height ?? window.innerHeight;
        document.body.classList.add('search-results-open');
        modalContainerRef.current.style.maxHeight = `${visualViewportHeight}px`;
        modalContainerRef.current.style.overflowY = 'auto';
      }
    };

    updateModalHeight();

    window.visualViewport?.addEventListener('resize', updateModalHeight);
    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', updateModalHeight);
    }

    return () => {
      document.body.classList.remove('search-results-open');
      if (modalContainerRef.current) {
        modalContainerRef.current.style.maxHeight = 'none';
        modalContainerRef.current.style.overflowY = 'hidden';
      }
      window.visualViewport?.removeEventListener('resize', updateModalHeight);
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', updateModalHeight);
      }
    };
  }, [open, viewPortBreakpoint]);

  if (open) {
    return (
      <div className="sayt-modal-container-wrapper hmf-mt-s">
        <div
          {...props}
          className={`sayt-modal-container ${newSayTEnabled ? 'new-suggestions' : ''}`}
          ref={modalContainerRef}>
          {children}
        </div>
      </div>
    );
  }
  return null;
};

export default SAYTModalWrapper;
